<script setup lang="ts">
import { getContainers } from '@/services/rc.service'
import type { IContainerDashboard, IContainerMiner } from '@/types/rc/containers'
import type { IContainerDashboardFilters } from '@/types/rc/filters'
import type { IContainer } from '@/types/rc/shipments'
import { ElMessage } from 'element-plus'
import { ref } from 'vue'

const props = defineProps<{
  miners: IContainerMiner[]
  originContainer: IContainer
}>()

const open = defineModel<boolean>()
const isLoading = ref<boolean>(false)
const emit = defineEmits(['close', 'moveMiners'])

// Container select refs
const initial = ref(true)
const selectedContainer = ref<IContainerDashboard>()
const containerFilterOptions = ref<{ label: string; value: IContainerDashboard }[]>([])
const containerFilterLoading = ref(false)

/**
 * Remote method for container filter.
 *
 * @param query container query fragment.
 */
const filterContainers = async (query: string) => {
  if (query == '') return

  initial.value = false

  try {
    containerFilterLoading.value = true

    const filters: IContainerDashboardFilters = {
      page: 1,
      per_page: 10,
      work_order_number: props.originContainer.work_order_number,
      kind: props.originContainer.kind,
      fragment: query,
    }

    const filteredContainers = await getContainers(filters)
    containerFilterOptions.value = filteredContainers.data
      .filter((c: IContainerDashboard) => c.number != props.originContainer.number)
      .map((c: IContainerDashboard) => {
        return {
          label: c.number,
          value: c,
        }
      })
  } catch {
    ElMessage.error('Failed to filter containers')
    containerFilterOptions.value = []
  } finally {
    containerFilterLoading.value = false
  }
}

/**
 * Handles form submit.
 * Create or updates a customer, depending on the form mode.
 *
 * @param formEl form element ref.
 */
const submit = async () => {
  isLoading.value = true
  emit('moveMiners', props.originContainer.id, selectedContainer.value?.id, props.miners)
}

/**
 * Reset form, reset mutations, emit closed (unsets customer ID prop).
 */
const handleClosed = () => {
  isLoading.value = false
  selectedContainer.value = undefined
  containerFilterOptions.value = []
  emit('close')
}
</script>

<template>
  <el-dialog v-model="open" :width="600" title="Move Miners" :show-close="false" @closed="handleClosed">
    <p>You are moving {{ props.miners.length }} miners to another container</p>
    <br />
    <div class="scrollable-list">
      <ul>
        <li v-for="miner in props.miners" :key="miner.id">{{ miner.model }} - {{ miner.sn }}</li>
      </ul>
    </div>
    <br />
    <div class="flex flex-col gap-2">
      <p>Choose a container</p>
      <el-select-v2
        v-model="selectedContainer"
        :options="containerFilterOptions"
        :loading="containerFilterLoading"
        remote
        :remote-method="filterContainers"
        filterable
        clearable
        value-key="id"
        placeholder="Search a container"
        loading-text="Loading containers..."
      >
        <template v-if="initial" #footer>
          <el-text> Start typing to lookup... </el-text>
        </template>
      </el-select-v2>
    </div>
    <template #footer>
      <el-button @click="open = false">Cancel</el-button>
      <el-button type="primary" :loading="isLoading" :disabled="!selectedContainer" @click="submit()">Move</el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.scrollable-list {
  max-height: 132px;
  overflow-y: auto;
}

ul {
  list-style-type: disc;
  list-style-position: inside;
}
</style>
